/**
 * Aide & Lexique — les quatre mécanismes de flexibilité, et d'où vient l'argent.
 *
 * Page entièrement statique : aucun appel réseau, aucune donnée client. Elle
 * explique ce que l'application pilote, ce qui manquait à tous les écrans qui
 * affichent MECAPA, NEBCO, MA ou R2 sans jamais dire ce que c'est.
 *
 * Contenu métier repris de la version construite par le client (itération 38),
 * mot pour mot : ce sont ses définitions et ses barèmes, pas les nôtres.
 */
import { Card, CardContent } from '@/shared/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import Breadcrumb from '@/shared/components/Breadcrumb';
import { BookOpen, Zap, Battery, Activity, Waves } from 'lucide-react';

const MECANISMES = [
  {
    key: 'MECAPA',
    Icon: Battery,
    accent: 'border-amber-200 bg-gradient-to-br from-amber-50 to-white',
    titre: 'MECAPA / AO — Mécanismes Capacitaires',
    principe: "Garantir une capacité de production ou de baisse de consommation en cas de tension sur le réseau, 1 à 5 fois par an.",
    remuneration: "Annuelle, sur base de la puissance mise à disposition (€/kW/an).",
  },
  {
    key: 'NEBCO',
    Icon: Zap,
    accent: 'border-cyan-200 bg-gradient-to-br from-cyan-50 to-white',
    titre: "NEBCO — Échange de blocs d'énergie",
    principe: "Baisser le niveau de soutirage du site lors des prix spot élevés pour revendre l'énergie sur les marchés.",
    remuneration: "Sur base de l'énergie modulée et des prix spot du marché (€/MWh).",
  },
  {
    key: 'MA',
    Icon: Activity,
    accent: 'border-emerald-200 bg-gradient-to-br from-emerald-50 to-white',
    titre: "MA — Mécanisme d'Ajustement",
    principe: "Rétablir l'équilibre production-consommation à court terme du réseau électrique.",
    remuneration: "Sur base de l'énergie ajustée et du prix de l'équilibrage donné par RTE.",
  },
  {
    key: 'R2',
    Icon: Waves,
    accent: 'border-violet-200 bg-gradient-to-br from-violet-50 to-white',
    titre: 'R2 — Réserve Secondaire',
    principe: "Maintenir l'équilibre production-consommation à très court terme (réponse < 1 min).",
    remuneration: "Sur base de la puissance mise à disposition, de l'énergie ajustée et du prix de clearing.",
  },
];

const ACTIONS = [
  {
    mecanisme: 'MECAPA',
    principe: 'Capacité garantie',
    equilibrage: false, energie: false, capacitaire: true,
    action: "1 à 5 activations par an sur signal RTE, durée de quelques heures",
  },
  {
    mecanisme: 'NEBCO',
    principe: "Blocs d'énergie sur prix spot",
    equilibrage: false, energie: true, capacitaire: false,
    action: "Effacement horaire selon les signaux du marché (EPEX Spot)",
  },
  {
    mecanisme: 'MA',
    principe: "Ajustement RTE court terme",
    equilibrage: true, energie: true, capacitaire: false,
    action: "Activation à la minute selon appel d'offres RTE",
  },
  {
    mecanisme: 'R2',
    principe: 'Réserve secondaire fréquence',
    equilibrage: true, energie: true, capacitaire: true,
    action: "Régulation automatique en moins d'une minute sur écart de fréquence",
  },
];

function Pastille({ actif }) {
  return actif
    ? <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 font-semibold text-emerald-700">✓</span>
    : <span className="text-slate-300">—</span>;
}

export default function AideLexiquePage() {
  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl" data-testid="aide-lexique-page">
      <div>
        <Breadcrumb items={[{ label: 'Aide & Lexique' }]} />
        <h1 className="flex items-center gap-2 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          <BookOpen className="h-6 w-6 text-slate-700" />
          Aide &amp; Lexique
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Comprendre les mécanismes de flexibilité électrique et leur logique de rémunération.
        </p>
      </div>

      <section data-testid="lexique-mecanismes">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-900">
          Lexique des mécanismes
        </h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {MECANISMES.map(({ key, Icon, accent, titre, principe, remuneration }) => (
            <Card key={key} className={`border ${accent}`} data-testid={`mech-card-${key}`}>
              <CardContent className="p-5">
                <div className="mb-3 flex items-center gap-2">
                  <Icon className="h-5 w-5 text-slate-800" />
                  <h3 className="text-[14px] font-semibold text-slate-900">{titre}</h3>
                </div>
                <div className="space-y-2 text-[12.5px] leading-relaxed text-slate-700">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Principe</p>
                    <p>{principe}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Rémunération</p>
                    <p>{remuneration}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section data-testid="tableau-actions">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-900">
          Tableau des actions par mécanisme
        </h2>
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mécanisme</TableHead>
                <TableHead>Principe</TableHead>
                <TableHead className="text-center">Équilibrage</TableHead>
                <TableHead className="text-center">Énergie</TableHead>
                <TableHead className="text-center">Capacitaire</TableHead>
                <TableHead>Activation</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ACTIONS.map((l) => (
                <TableRow key={l.mecanisme} data-testid={`actions-row-${l.mecanisme}`}>
                  <TableCell className="font-semibold text-slate-900">{l.mecanisme}</TableCell>
                  <TableCell className="text-[12px] text-slate-700">{l.principe}</TableCell>
                  <TableCell className="text-center"><Pastille actif={l.equilibrage} /></TableCell>
                  <TableCell className="text-center"><Pastille actif={l.energie} /></TableCell>
                  <TableCell className="text-center"><Pastille actif={l.capacitaire} /></TableCell>
                  <TableCell className="text-[12px] text-slate-600">{l.action}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <p className="border-t border-slate-100 pt-4 text-[11px] italic text-slate-400">
        Sources : documentation RTE, EPEX Spot et Enedis · références Symphonics et Survoltage (barèmes en vigueur).
      </p>
    </div>
  );
}
